// 📦 Import de React et du hook useState
import React, { useState, useEffect } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { logDev } from "../../utils/logger.js"

// 🎨 Import des composants Material-UI pour la mise en page et les interactions
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  TextField,
  Button,
  IconButton
} from "@mui/material";

// 🔽 Icônes Material-UI utilisées dans le composant
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { mapMoment, getMomentColor, moments } from "../../constants/moments.js"

// 🧩 Hooks et contextes personnalisés
import { useCustomBlocks } from "../../hooks/useCustomBlocks.js";
import { useGroupe } from "../../context/GroupeContext.js";
import { useAdminRole } from "../../hooks/useAdminRole.js";
import { useLanguage } from '../../context/LanguageContext.js';

// ------------------------------------------------------------
// Composant BlocPersonnalise
// ------------------------------------------------------------
const BlocPersonnalise = () => {
  // 🌍 Gestion de la traduction
  const { t } = useLanguage();

  // 🔑 Récupération de l’ID du groupe et du rôle de l’utilisateur
  const { groupeId } = useGroupe();
  const { groupeActif } = useGroupe(); 
  const role = useAdminRole(groupeId);
  const isAdmin = role === "admin";
  const [expandedBlock, setExpandedBlock] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState(null);

  // 📦 Hooks personnalisés pour gérer les blocs et items
  const {
    customBlocks,
    newBlockName,
    setNewBlockName,
    addCustomBlock,
    handleCustomItemChange,
    addCustomItem,
    removeCustomItem,
    renameCustomBlock,
    removeCustomBlock,
    saveCustomItem
  } = useCustomBlocks();

  // ✏️ State pour gérer les noms édités et les items sauvegardés
  const [editedNames, setEditedNames] = useState({});
  const [savedItems, setSavedItems] = useState({}); // { "block-0": true }

    // 🔹 Log global optimisé
    useEffect(() => {
      logDev("[BlocPersonnalise] état global", {
        nbBlocks: Object.keys(customBlocks).length,
        blocks: Object.keys(customBlocks).map(block => ({
          name: block,
          nbItems: customBlocks[block].length
        }))
      });
    }, [customBlocks]);

  // 🔄 Gestion du changement de titre d’un bloc
  const handleTitleChange = (oldName, newName) => {
    setEditedNames((prev) => ({ ...prev, [oldName]: newName }));
  };

  // ✅ Validation du nouveau nom de bloc quand on sort du champ
  const handleTitleBlur = (oldName) => {
    const newName = editedNames[oldName]?.trim();
    if (newName && newName !== oldName && !customBlocks[newName]) {
      renameCustomBlock(oldName, newName);
      setEditedNames((prev) => {
        const updatedNames = { ...prev };
        delete updatedNames[oldName];
        return updatedNames;
      });
    }
  };

  // 💾 Sauvegarde d’un item
  const handleSaveClick = (block, index, item) => {
    if (saveCustomItem) {
      saveCustomItem(block, index, item);
    }
    const id = `${block}-${index}`;
    setSavedItems((prev) => ({ ...prev, [id]: true }));
  };

  // ✏️ Modification d’un item (et suppression de la coche "sauvegardé")
  const handleChange = (block, index, field, value) => {
    handleCustomItemChange(block, index, field, value);
    
    // Si l'utilisateur modifie, on enlève la coche
    const id = `${block}-${index}`;
    setSavedItems((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  // 🎨 Rendu JSX
  return (
    <Box sx={{ maxWidth: 1000, width: "100%", marginX: "auto" }}>
      {/* 🔁 Boucle sur chaque bloc personnalisé */}
      {Object.keys(customBlocks).map((block) => (
        <Accordion
          key={block}
          expanded={expandedBlock === block}
          onChange={() => setExpandedBlock(expandedBlock === block ? null : block)}
          sx={{
            marginBottom: 2,
            backgroundColor: "#fffdf2 !important"
          }}
        >
          {/* 🏷️ En-tête du bloc avec champ de texte pour renommer */}
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TextField
              variant="standard"
              value={editedNames[block] ?? block}
              onChange={(e) => handleTitleChange(block, e.target.value)}
              onBlur={() => handleTitleBlur(block)}
              size="small"
              disabled={!isAdmin} 
              sx={{ fontSize: "1.2rem", fontWeight: "bold", width: "100%" }}
            />
          </AccordionSummary>

          {/* 📋 Contenu du bloc : liste des items */}
          <AccordionDetails>
            {customBlocks[block].map((item, i) => {
              const id = `${block}-${i}`;
              const isSaved = item.saved || savedItems[id];
              return (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 2,
                    marginBottom: 1,
                    backgroundColor: getMomentColor("Custom"),
                  }}
                >
                  {/* Champ texte pour le nom de l’item */}
                  <TextField
                    label={t('Item')}
                    value={item.name}
                    disabled={!isAdmin}
                    sx={{ 
                      flex: 1,
                      minWidth: { xs: "100%", sm: "300px", md: "460px" }, // 👈 responsive 
                      backgroundColor: "#fff !important", 
                      borderRadius: 1,
                      ...(isSaved && {
                      backgroundColor: "#f0f0f0", // gris clair
                      opacity: 0.6,              // effet visuel d'élément "désactivé"
                      borderRadius: 1,
                      padding: "4px 8px"
                    })
                  }}
                    onChange={(e) => handleChange(block, i, "name", e.target.value)}
                    size="small"
                  />

                  {/* Champ numérique pour la quantité */}
                  <TextField
                    label={t('Quantity')}
                    type="number"
                    inputProps={{
                      min: 0,
                      step: 1,
                      pattern: "[0-9]*", // 👈 empêche la saisie de virgule/point sur mobile
                      inputMode: "numeric", // 👈 clavier numérique sur mobile
                      style: { width: "80px", textAlign: "center" }
                    }}
                    value={item.quantity}
                    disabled={!isAdmin}
                    onChange={(e) => {
                      // on force la valeur à un entier
                      const parsed = parseInt(e.target.value, 10);
                      handleChange(block, i, "quantity", isNaN(parsed) ? "" : parsed);
                    }}
                    size="small"
                    error={Boolean(item.name) && !item.quantity && item.quantity !== 0}
                    helperText=""
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: 1,
                      width: { xs: "100%", sm: "160px", md: "120px" }, // 👈 responsive
                      ...(isSaved && {
                      backgroundColor: "#f0f0f0",
                      opacity: 0.6
                    })
                 }}
                  />
                  {/* Bouton enregistrer ou coche verte */}
                  {isSaved ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <IconButton
                      onClick={() => handleSaveClick(block, i, item)}
                      sx={{
                      // Toujours grossi si un item est saisi et pas encore sauvegardé
                        transform: (Boolean(item.name) || Boolean(item.quantity)) && !isSaved ? "scale(1.4)" : "scale(1)",
                        transition: "transform 0.2s ease"
                      }}
                    >
                    <SaveIcon
                    sx={{
                    // Toujours rouge si un item est saisi et pas encore sauvegardé
                      color: (Boolean(item.name) || Boolean(item.quantity)) && !isSaved
                        ? "red"
                        : "#71685e",
                      fontSize: "2rem"
                    }}
                   />
                  </IconButton>
                )}
                  {/* Bouton pour supprimer un item */}
                  {isAdmin && (
                  <Button
                    onClick={() => removeCustomItem(block, i)}
                    variant="outlined"
                    color="error"
                    size="small"
                  >
                    ➖
                  </Button>
                  )}
                </Box>
              );
            })}
            {/* Bouton pour ajouter un nouvel item (admin uniquement) */}
            {isAdmin && (
            <Button
              onClick={() => addCustomItem(block)}
              variant="outlined"
              startIcon={<AddIcon />}
              className="add-item-button"
              color="primary"
              size="small"
            >
              {t('AddAnItem')}
            </Button>
            )}
          </AccordionDetails>

          {isAdmin && (
            <Button
              onClick={() => {
                setBlockToDelete(block);
                setConfirmOpen(true);
              }}
              variant="outlined"
              color="error"
              size="small"
              sx={{ mt: 2 }}
            >
            🗑️ {t("DeleteBlock")}
          </Button>
        )}
        </Accordion>
      ))}

      {/* Section pour ajouter un nouveau bloc (admin uniquement) */}
      {isAdmin && (
      <Box sx={{ marginTop: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ textAlign: "left" }}>
          🆕 {t('AddANewBlock')}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, marginTop: 1 }}>
          <TextField
            label={t('BlocName')}
            sx={{
              marginBottom: 2,
              backgroundColor: "#fff !important"
            }}
            size="small"
            fullWidth
            value={newBlockName}
            onChange={(e) => setNewBlockName(e.target.value)}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            className="add-block-button"
            onClick={() => {
              addCustomBlock(newBlockName);
            setExpandedBlock(newBlockName); // 👈 ouvre le bloc automatiquement
            }}
          >
            {t('Add')}
          </Button>
        </Box>
      </Box>
      )}

      <Snackbar
      open={snackbarOpen}
      autoHideDuration={3000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
      <Alert
      onClose={() => setSnackbarOpen(false)}
      severity="success"
      sx={{ width: "100%" }}
      >
      {snackbarMessage}
      </Alert>
      </Snackbar>

    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
      <DialogTitle>{t("ConfirmDeleteBlockTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
          {t("ConfirmDeleteBlockText", { block: blockToDelete })}
          </Typography>
        </DialogContent>

      <DialogActions>
        <Button onClick={() => setConfirmOpen(false)}>{t("Cancel")}</Button>
        <Button
          onClick={() => {
            removeCustomBlock(blockToDelete);
            setExpandedBlock(null);
            setConfirmOpen(false);
            setSnackbarMessage(t("BlockDeleted"));
            setSnackbarOpen(true);
          }}
          color="error"
          variant="contained"
        >
        {t("Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
    </Box>
  );
};

export default BlocPersonnalise;
